<template>
  <section class="availability" :aria-busy="isLoading ? 'true' : 'false'">
    <template v-if="!showTechnologyChoice">
      <form class="form" autocomplete="off" novalidate @submit.prevent="checkAvailability">
        <header class="header">
          <h2>{{ headline }}</h2>
          <button class="reset" type="button" title="Adresse zurücksetzen" aria-label="Adresse zurücksetzen" @click="resetForm">
            &#8635;
          </button>
        </header>

        <div class="fields">
          <div class="field field-postal">
            <AvailabilityCombobox
              v-model="postalInput"
              input-id="postal-code"
              label="Wohnort oder PLZ"
              placeholder="Wohnort oder PLZ"
              inputmode="numeric"
              autocomplete="off"
              :options="postalOptions"
              :disabled="loadingPostalCodes"
              @input="selectPostalCode"
            />
          </div>

          <div class="field field-street">
            <AvailabilityCombobox
              v-model="streetInput"
              input-id="street"
              label="Straße"
              placeholder="Straße"
              autocomplete="off"
              :options="streetOptions"
              :disabled="!selectedPostalCode || loadingStreets"
              @input="selectStreet"
            />
          </div>

          <div class="field field-number">
            <AvailabilityCombobox
              v-model="streetNumberInput"
              input-id="street-number"
              label="Hausnummer"
              placeholder="Nummer"
              autocomplete="off"
              :options="streetNumberSelectOptions"
              :disabled="!selectedStreet || loadingStreetNumbers"
              @input="selectStreetNumber"
              @select="confirmStreetNumber"
              @submit="confirmStreetNumber"
            />
          </div>

          <button class="primary" type="submit" :disabled="!addressDetails || !addressDetails.checkoutLink">
            Jetzt prüfen
          </button>
        </div>

        <p v-if="loadingMessage" class="status" role="status">{{ loadingMessage }}</p>
        <p v-if="formError" class="message error" role="alert">{{ formError }}</p>
        <p v-else-if="showAddressNotFound" class="message" role="status">
          Ist die Adresse richtig? Wir haben keinen passenden Eintrag in unserer Datenbank gefunden. Melde dich bei unserem Service, wir sehen, was sich machen lässt.
        </p>
        <p v-else-if="addressDetails && !addressDetails.checkoutLink" class="message" role="status">
          Deine Adresse wird aktuell bearbeitet. Schaue später noch einmal vorbei, wenn die Bestellstrecke freigeschaltet wurde.
        </p>
      </form>
    </template>

    <div v-else class="technology">
      <h2>Folgende Tarife sind an deiner Adresse verfügbar:</h2>
      <div class="technology-actions">
        <a class="primary" :href="fiberUrl" target="_blank" rel="noopener noreferrer" @click="emitCheckout('fiber')">Glasfaser</a>
        <span>oder</span>
        <a class="primary" :href="vdslUrl" target="_blank" rel="noopener noreferrer" @click="emitCheckout('vdsl')">VDSL</a>
      </div>
      <button class="back" type="button" @click="showTechnologyChoice = false">Zurück zum Verfügbarkeitscheck</button>
    </div>
  </section>
</template>

<script>
import {
  buildCheckoutUrl,
  mapStreetNumberOption,
  normalizeStreetName,
  normalizeStreetNumberQuery,
  sortPostalCodeOptionsByCity,
  sortStreetNumberOptions
} from '@/services/availabilityCheck'
import AvailabilityCombobox from './AvailabilityCombobox'

const responseCache = new Map()

export default {
  name: 'AvailabilityCheckWidget',
  components: {
    AvailabilityCombobox
  },
  props: {
    apiBase: {
      type: String,
      default: ''
    },
    customerGroup: {
      type: String,
      default: ''
    },
    integrationCode: {
      type: String,
      default: 'legacy'
    },
    headline: {
      type: String,
      default: 'Prüfe, ob TKRZ Produkte bei dir verfügbar sind:'
    }
  },
  data () {
    return {
      postalInput: '',
      streetInput: '',
      streetNumberInput: '',
      postalOptions: [],
      streets: [],
      streetNumberOptions: [],
      selectedPostalCode: null,
      selectedStreet: '',
      addressDetails: null,
      loadingPostalCodes: false,
      loadingStreets: false,
      loadingStreetNumbers: false,
      streetNumberTouched: false,
      trackedSearches: new Set(),
      formError: '',
      showTechnologyChoice: false,
      requestControllers: {}
    }
  },
  computed: {
    normalizedApiBase () {
      const fallback = `${window.location.origin}/api`
      return String(this.apiBase || fallback).replace(/\/+$/, '')
    },
    isLoading () {
      return this.loadingPostalCodes || this.loadingStreets || this.loadingStreetNumbers
    },
    loadingMessage () {
      if (this.loadingPostalCodes) return 'Orte werden geladen ...'
      if (this.loadingStreets) return 'Straßen werden geladen ...'
      if (this.loadingStreetNumbers) return 'Hausnummern werden geladen ...'
      return ''
    },
    showAddressNotFound () {
      return this.streetNumberTouched && Boolean(this.streetNumberInput) && !this.addressDetails && !this.loadingStreetNumbers
    },
    fiberUrl () {
      return buildCheckoutUrl(this.addressDetails, { legacy: true })
    },
    vdslUrl () {
      return buildCheckoutUrl(this.addressDetails, { legacy: true, vdsl: true })
    },
    streetOptions () {
      return this.streets.map(street => ({ value: street, label: street }))
    },
    streetNumberSelectOptions () {
      return this.streetNumberOptions.map(option => ({
        ...option,
        label: option.text
      }))
    }
  },
  mounted () {
    this.loadPostalCodes()
    this.dispatchWidgetEvent('tkrz:availability-ready', {})
  },
  beforeUnmount () {
    Object.values(this.requestControllers).forEach(controller => controller.abort())
  },
  methods: {
    async fetchJson (path, cacheSeconds, requestKey) {
      const url = `${this.normalizedApiBase}${path}`
      const cached = responseCache.get(url)
      const now = Date.now()
      if (cached && cached.expiresAt > now) return cached.data

      if (requestKey && this.requestControllers[requestKey]) {
        this.requestControllers[requestKey].abort()
      }
      const controller = new AbortController()
      if (requestKey) this.requestControllers[requestKey] = controller

      const response = await fetch(url, {
        headers: { Accept: 'application/json' },
        credentials: 'omit',
        signal: controller.signal
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const data = await response.json()
      responseCache.set(url, { data, expiresAt: now + cacheSeconds * 1000 })
      return data
    },
    async loadPostalCodes () {
      this.loadingPostalCodes = true
      try {
        const data = await this.fetchJson('/verfuegbarkeitscheck/get/plz', 300, 'postalCodes')
        this.postalOptions = sortPostalCodeOptionsByCity(data.map(item => {
          const key = `${item.plz}|${item.city}`
          return {
            key,
            value: key,
            label: `${item.plz} (${item.city})`,
            group: item.city,
            plz: String(item.plz),
            city: item.city
          }
        }))
        this.formError = ''
      } catch (error) {
        if (error.name !== 'AbortError') this.formError = 'Die Adressauswahl konnte nicht geladen werden. Bitte versuche es erneut.'
      } finally {
        this.loadingPostalCodes = false
      }
    },
    selectPostalCode () {
      const selected = this.postalOptions.find(option => option.label === this.postalInput) || null
      if (selected?.key === this.selectedPostalCode?.key) return

      this.selectedPostalCode = selected
      this.streetInput = ''
      this.selectedStreet = ''
      this.streets = []
      this.clearStreetNumber()
      if (selected) this.loadStreets(selected)
    },
    async loadStreets (postalCode) {
      this.loadingStreets = true
      try {
        const path = `/verfuegbarkeitscheck/get/street/${encodeURIComponent(postalCode.plz)}/${encodeURIComponent(postalCode.city)}`
        const data = await this.fetchJson(path, 300, 'streets')
        if (this.selectedPostalCode?.key !== postalCode.key) return
        this.streets = [...new Set(data.map(item => normalizeStreetName(item.street)))].sort((a, b) => a.localeCompare(b, 'de'))
        this.formError = ''
      } catch (error) {
        if (error.name !== 'AbortError') this.formError = 'Die Straßen konnten nicht geladen werden. Bitte versuche es erneut.'
      } finally {
        this.loadingStreets = false
      }
    },
    selectStreet () {
      const selected = this.streets.find(street => street === this.streetInput) || ''
      if (selected === this.selectedStreet) return

      this.selectedStreet = selected
      this.clearStreetNumber()
      if (selected) this.loadStreetNumbers(selected)
    },
    async loadStreetNumbers (street) {
      const postalCode = this.selectedPostalCode
      if (!postalCode) return

      this.loadingStreetNumbers = true
      try {
        const path = `/verfuegbarkeitscheck/get/streetnumber/${encodeURIComponent(postalCode.plz)}/${encodeURIComponent(postalCode.city)}/${encodeURIComponent(street)}`
        const data = await this.fetchJson(path, 60, 'streetNumbers')
        if (this.selectedStreet !== street) return
        this.streetNumberOptions = sortStreetNumberOptions(data.map(mapStreetNumberOption))
        this.formError = ''
      } catch (error) {
        if (error.name !== 'AbortError') this.formError = 'Die Hausnummern konnten nicht geladen werden. Bitte versuche es erneut.'
      } finally {
        this.loadingStreetNumbers = false
      }
    },
    selectStreetNumber () {
      this.streetNumberTouched = false
      const query = normalizeStreetNumberQuery(this.streetNumberInput)
      const option = this.streetNumberOptions.find(item => normalizeStreetNumberQuery(item.text) === query)
      this.addressDetails = option?.details || null
      if (option) this.streetNumberInput = option.text

      const groupId = Number.parseInt(this.customerGroup, 10)
      if (this.addressDetails && Number.isInteger(groupId) && groupId > 0) {
        this.addressDetails.customergroup = { groupId }
      }

      if (this.addressDetails) {
        this.dispatchWidgetEvent('tkrz:availability-result', { address: this.addressDetails })
      }
    },
    confirmStreetNumber () {
      if (!this.streetNumberInput) return
      const query = normalizeStreetNumberQuery(this.streetNumberInput)
      const option = this.streetNumberOptions.find(item => normalizeStreetNumberQuery(item.text) === query)
      this.addressDetails = option?.details || null
      if (option) this.streetNumberInput = option.text
      this.streetNumberTouched = true
      this.trackStreetNumberSearch(Boolean(option))
    },
    trackStreetNumberSearch (matched) {
      const postalCode = this.selectedPostalCode
      if (!postalCode || !this.selectedStreet || !this.streetNumberInput) return
      const payload = {
        plz: postalCode.plz,
        city: postalCode.city,
        street: this.selectedStreet,
        houseNumber: this.streetNumberInput,
        matched,
        source: 'widget',
        integrationCode: this.normalizedIntegrationCode()
      }
      const key = JSON.stringify(payload)
      if (this.trackedSearches.has(key)) return
      this.trackedSearches.add(key)
      fetch(`${this.normalizedApiBase}/verfuegbarkeitscheck/track`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'omit',
        body: JSON.stringify(payload)
      }).catch(() => {})
    },
    clearStreetNumber () {
      this.streetNumberInput = ''
      this.streetNumberOptions = []
      this.streetNumberTouched = false
      this.addressDetails = null
      this.showTechnologyChoice = false
    },
    resetForm () {
      Object.values(this.requestControllers).forEach(controller => controller.abort())
      this.postalInput = ''
      this.streetInput = ''
      this.streetNumberInput = ''
      this.streets = []
      this.streetNumberOptions = []
      this.selectedPostalCode = null
      this.selectedStreet = ''
      this.addressDetails = null
      this.formError = ''
      this.streetNumberTouched = false
      this.showTechnologyChoice = false
    },
    checkAvailability () {
      if (!this.addressDetails?.checkoutLink) return
      if (this.addressDetails.vdsl) {
        this.showTechnologyChoice = true
        return
      }

      const url = this.fiberUrl
      if (url) {
        this.emitCheckout('fiber')
        window.open(url, '_blank', 'noopener')
      }
    },
    emitCheckout (technology) {
      this.dispatchWidgetEvent('tkrz:availability-checkout', {
        technology,
        address: this.addressDetails
      })
    },
    dispatchWidgetEvent (name, detail) {
      this.$el.dispatchEvent(new CustomEvent(name, {
        detail,
        bubbles: true,
        composed: true
      }))
    },
    normalizedIntegrationCode () {
      const code = String(this.integrationCode || '').trim().toLocaleLowerCase('de-DE')
      return /^[a-z0-9][a-z0-9._-]{0,63}$/.test(code) ? code : 'legacy'
    }
  }
}
</script>

<style>
:host {
  display: block;
  color: var(--tkrz-text-color, inherit);
  font-family: var(--tkrz-font-family, inherit);
  container-type: inline-size;
}

* {
  box-sizing: border-box;
}

.availability {
  width: 100%;
  padding: 16px 8px 24px;
  background: var(--tkrz-background, transparent);
}

.header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

h2 {
  margin: 0;
  font: inherit;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: 0;
}

.reset,
.back {
  border: 0;
  color: inherit;
  background: transparent;
  cursor: pointer;
}

.reset {
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  padding: 0;
  font-size: 28px;
  line-height: 44px;
}

.reset:hover,
.reset:focus-visible,
.back:hover,
.back:focus-visible {
  text-decoration: underline;
}

.fields {
  display: grid;
  grid-template-columns: minmax(180px, 4fr) minmax(180px, 4fr) minmax(110px, 2fr) minmax(130px, 2fr);
  gap: 16px;
  align-items: end;
}

.field {
  min-width: 0;
}

label {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

input {
  width: 100%;
  height: 46px;
  padding: 10px 12px;
  border: 1px solid var(--tkrz-border-color, #8b949e);
  border-radius: 5px;
  color: #17191c;
  background: #fff;
  font: inherit;
  font-size: 16px;
  letter-spacing: 0;
}

input:focus-visible,
button:focus-visible,
a:focus-visible {
  outline: 3px solid var(--tkrz-focus-color, #f3b21b);
  outline-offset: 2px;
}

input:disabled {
  color: #555b61;
  background: #edf0f2;
  cursor: not-allowed;
}

/*
 * These styles intentionally live in the custom element's root component.
 * Vue CLI only injects root component styles into the widget's Shadow DOM;
 * styles owned by child SFCs are otherwise emitted for the regular app only.
 */
.availability-combobox {
  position: relative;
}

.availability-combobox .input-group {
  position: relative;
}

.availability-combobox input {
  padding-right: 44px;
}

.availability-combobox .toggle {
  position: absolute;
  top: 1px;
  right: 1px;
  bottom: 1px;
  display: flex;
  width: 42px;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 0 4px 4px 0;
  color: inherit;
  background: transparent;
  cursor: pointer;
}

.availability-combobox .toggle:disabled {
  cursor: not-allowed;
}

.availability-combobox .toggle span {
  width: 0;
  height: 0;
  border-top: 6px solid currentColor;
  border-right: 5px solid transparent;
  border-left: 5px solid transparent;
}

.availability-combobox .options {
  position: absolute;
  z-index: 10;
  top: calc(100% + 4px);
  right: 0;
  left: 0;
  max-height: 640px;
  padding: 4px 0;
  margin: 0;
  overflow-y: auto;
  list-style: none;
  border: 1px solid var(--tkrz-border-color, #8b949e);
  border-radius: 5px;
  background: #fff;
  box-shadow: 0 8px 18px rgb(23 25 28 / 18%);
}

.availability-combobox .option {
  display: block;
  width: 100%;
  padding: 10px 12px;
  border: 0;
  color: #17191c;
  background: #fff;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.availability-combobox .option-group {
  padding: 8px 12px 4px;
  color: #25292d;
  background: #edf0f2;
  font-size: 14px;
  font-weight: 700;
}

.availability-combobox .option:hover,
.availability-combobox .option:focus-visible,
.availability-combobox .option.active {
  color: #17191c;
  background: #edf0f2;
}

.availability-combobox .no-results {
  padding: 10px 12px;
  color: #555b61;
  font-size: 14px;
}

.primary {
  display: inline-flex;
  min-height: 46px;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border: 0;
  border-radius: 5px;
  color: #fff;
  background: var(--tkrz-button-color, #e84e0f);
  font: inherit;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.4;
  letter-spacing: 0;
  text-decoration: none;
  cursor: pointer;
}

.primary:hover {
  background: var(--tkrz-button-hover-color, #c94009);
}

.primary:disabled {
  color: #202428;
  background: #d8dce0;
  cursor: not-allowed;
}

.status,
.message {
  margin: 14px 0 0;
  font-size: 15px;
  line-height: 1.5;
}

.message {
  padding: 12px;
  border-left: 4px solid #f3b21b;
  color: #17191c;
  background: #fff7d8;
}

.message.error {
  border-color: #c62828;
  background: #ffebee;
}

.technology {
  min-height: 160px;
}

.technology-actions {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-top: 24px;
}

.back {
  display: block;
  margin: 24px 0 0 auto;
  padding: 8px 0;
  font: inherit;
  font-weight: 600;
}

@container (max-width: 760px) {
  .fields {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .field-postal,
  .field-street {
    grid-column: 1 / -1;
  }
}

@container (max-width: 430px) {
  h2 {
    font-size: 22px;
  }

  .fields {
    grid-template-columns: 1fr;
  }

  .field-postal,
  .field-street {
    grid-column: auto;
  }

  .technology-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .technology-actions span {
    text-align: center;
  }
}
</style>
